import React, { useMemo } from "react";
import { 
  FiCheckCircle, 
  FiClock, 
  FiAlertTriangle, 
  FiActivity, 
  FiArrowRight, 
  FiTrendingUp, 
  FiShield, 
  FiFileText,
  FiPlus,
  FiInbox
} from "react-icons/fi";
import type { RcaRecordDto } from "../../../../types/rca.types.js";

export type RcaDashboardPageProps = {
  records: readonly RcaRecordDto[];
  onSelectRecord: (record: RcaRecordDto) => void;
  onCreateNew: () => void;
};

export function RcaDashboardPage({
  records,
  onSelectRecord,
  onCreateNew,
}: RcaDashboardPageProps) {
  const inProgressCount = records.filter(r => r.statusCode === "RCA_IN_PROGRESS" || r.statusCode === "IN_PROGRESS" || !r.statusCode).length;
  const underReviewCount = records.filter(r => r.statusCode === "RCA_UNDER_REVIEW" || r.statusCode === "UNDER_REVIEW").length;
  const completedCount = records.filter(r => r.statusCode === "RCA_COMPLETED" || r.statusCode === "COMPLETED").length;
  const overdueCount = records.filter(r => r.isOverdue || r.statusCode === "RCA_OVERDUE" || r.statusCode === "OVERDUE").length;

  const methodUtilization = useMemo(() => {
    const total = records.length;
    const catalog = [
      { name: "5-Why Analysis", code: "FIVE_WHY", color: "bg-blue-600" },
      { name: "Fishbone (Ishikawa)", code: "FISHBONE", color: "bg-indigo-600" },
      { name: "8D Problem Solving", code: "8D", color: "bg-cyan-600" },
      { name: "Fault Tree Analysis", code: "FTA", color: "bg-amber-600" },
      { name: "Pareto Analysis", code: "PARETO", color: "bg-emerald-600" },
      { name: "Process Mapping", code: "PROCESS_MAP", color: "bg-rose-600" },
      { name: "Is / Is Not Analysis", code: "IS_IS_NOT", color: "bg-purple-600" },
      { name: "Brainstorming", code: "BRAINSTORMING", color: "bg-teal-600" },
    ];

    return catalog.map(m => {
      const count = records.filter(r => 
        (r.rcaMethodCode && r.rcaMethodCode.toUpperCase() === m.code) ||
        (r.rcaMethodName && r.rcaMethodName.toLowerCase().includes(m.name.toLowerCase().split(" ")[0]!))
      ).length;
      const pctNumber = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        ...m,
        count,
        pct: `${pctNumber}%`,
      };
    });
  }, [records]);

  return (
    <div className="space-y-6 bg-slate-50/50 p-1">
      {/* Top Banner with Action */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; RCA Owner Portal</div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">RCA Analytics & Operations Dashboard</h1>
          <p className="text-xs text-slate-500 mt-1">
            Real-time database metrics, investigation progress, and cross-method root cause verification.
          </p>
        </div>

        <button
          type="button"
          onClick={onCreateNew}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
        >
          <FiPlus className="w-4 h-4" />
          Create New RCA
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total RCAs */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Database RCAs</span>
            <div className="text-2xl font-bold text-slate-900 mt-1">{records.length}</div>
            <span className="text-[11px] text-slate-500 font-medium mt-1 block">
              {completedCount} Completed
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl font-bold border border-blue-100">
            <FiActivity className="w-6 h-6" />
          </div>
        </div>

        {/* In Progress */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">In Progress</span>
            <div className="text-2xl font-bold text-sky-700 mt-1">{inProgressCount}</div>
            <span className="text-[11px] text-slate-500 font-medium mt-1 block">Active investigations</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold border border-sky-100">
            <FiClock className="w-6 h-6" />
          </div>
        </div>

        {/* Under Review */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Under Review</span>
            <div className="text-2xl font-bold text-amber-700 mt-1">{underReviewCount}</div>
            <span className="text-[11px] text-slate-500 font-medium mt-1 block">Pending Quality Approval</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl font-bold border border-amber-100">
            <FiShield className="w-6 h-6" />
          </div>
        </div>

        {/* Overdue */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Overdue / Critical</span>
            <div className="text-2xl font-bold text-rose-700 mt-1">{overdueCount}</div>
            <span className="text-[11px] text-rose-600 font-semibold mt-1 block">
              {overdueCount > 0 ? "Requires escalation" : "All SLAs on track"}
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl font-bold border border-rose-100">
            <FiAlertTriangle className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Main Grid: Active RCA Work Queue + Method Usage Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active RCAs Table (2 Cols) */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col justify-between">
          <div>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Active RCA Investigations</h3>
                <p className="text-xs text-slate-500">Live records from database</p>
              </div>
            </div>

            {records.length === 0 ? (
              <div className="p-12 text-center space-y-3">
                <FiInbox className="w-10 h-10 text-slate-300 mx-auto" />
                <h4 className="text-sm font-bold text-slate-700">No RCA Records in Database</h4>
                <p className="text-xs text-slate-400 max-w-sm mx-auto">
                  There are currently no active RCA records. Click below to create your first RCA investigation.
                </p>
                <button
                  type="button"
                  onClick={onCreateNew}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm inline-flex items-center gap-1.5 mt-2"
                >
                  <FiPlus className="w-4 h-4" /> Create RCA
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">RCA Code</th>
                      <th className="py-3 px-3">Complaint</th>
                      <th className="py-3 px-3">Method</th>
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 px-3">SLA Status</th>
                      <th className="py-3 px-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {records.slice(0, 10).map(record => (
                      <tr 
                        key={record.id}
                        onClick={() => onSelectRecord(record)}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="py-3.5 px-4 font-bold text-blue-600">
                          {record.rcaCode}
                        </td>
                        <td className="py-3.5 px-3 text-slate-800 font-medium">
                          {record.complaintCode || record.complaintId || "-"}
                        </td>
                        <td className="py-3.5 px-3">
                          <span className="font-semibold text-slate-700">
                            {record.rcaMethodName || record.rcaMethodCode || "5-Why"}
                          </span>
                        </td>
                        <td className="py-3.5 px-3">
                          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-50 text-sky-700 border border-sky-200">
                            {record.statusName || record.statusCode || "In Progress"}
                          </span>
                        </td>
                        <td className="py-3.5 px-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                            record.isOverdue 
                              ? "bg-rose-50 text-rose-700 border-rose-200" 
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}>
                            {record.slaDaysText || (record.isOverdue ? "Overdue" : "On Track")}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectRecord(record);
                            }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-[11px] font-semibold shadow-sm inline-flex items-center gap-1"
                          >
                            Open <FiArrowRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Method Distribution Chart Panel (1 Col) */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Live Method Utilization</h3>
            <p className="text-xs text-slate-500 mb-4">Calculated in real-time from active database records</p>

            <div className="space-y-3.5">
              {methodUtilization.map(m => (
                <div key={m.name} className="space-y-1">
                  <div className="flex justify-between text-xs font-semibold text-slate-700">
                    <span>{m.name}</span>
                    <span className="text-slate-500">{m.count} ({m.pct})</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${m.color} rounded-full transition-all duration-500`} style={{ width: m.pct }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
